import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Xmark, ShareAndroid, Check } from "iconoir-react";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export default function InviteDialog({ open, onClose, code, householdName }) {
    const { t } = useT();
    const [copied, setCopied] = useState(false);
    const inviteUrl = `${window.location.origin}/?code=${code}`;

    const copyUrl = async () => {
        await navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        toast.success(t("invite.link_copied"));
        setTimeout(() => setCopied(false), 1500);
    };

    const share = async () => {
        const payload = {
            title: t("invite.share_title", { name: householdName }),
            text: t("invite.share_text", { name: householdName, code }),
            url: inviteUrl,
        };
        try {
            if (navigator.share) {
                await navigator.share(payload);
            } else {
                await copyUrl();
            }
        } catch (e) {
            if (e?.name !== "AbortError") toast.error(t("invite.share_cancelled"));
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-[#0D2A1B]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={onClose}
                    data-testid="invite-dialog"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={(e) => e.stopPropagation()}
                        className="brut-card p-6 sm:p-8 w-full max-w-md"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#255636] mb-1">
                                    {t("invite.eyebrow")}
                                </div>
                                <div className="font-heading text-2xl font-black tracking-tight">
                                    {t("invite.join_headline", { name: householdName })}
                                </div>
                            </div>
                            <button
                                data-testid="close-invite-btn"
                                onClick={onClose}
                                className="w-9 h-9 brut-border rounded-lg bg-white flex items-center justify-center hover:bg-[#FBF6EB]"
                            >
                                <Xmark className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex flex-col items-center gap-4 py-2">
                            <div className="bg-white brut-border rounded-2xl p-4 brut-shadow">
                                <QRCodeSVG
                                    value={inviteUrl}
                                    size={180}
                                    level="M"
                                    bgColor="#FFFFFF"
                                    fgColor="#0D2A1B"
                                    includeMargin={false}
                                />
                            </div>
                            <div className="text-center">
                                <div className="text-xs uppercase tracking-widest font-bold text-[#255636]">
                                    {t("invite.or_share_code")}
                                </div>
                                <div
                                    data-testid="invite-code-display"
                                    className="font-heading font-black text-4xl tracking-[0.15em] mt-1"
                                >
                                    {code}
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 space-y-2">
                            <div className="brut-border bg-white rounded-xl p-3 flex items-center gap-2">
                                <div
                                    className="flex-1 text-sm font-medium truncate"
                                    data-testid="invite-url"
                                >
                                    {inviteUrl}
                                </div>
                                <button
                                    data-testid="copy-invite-url-btn"
                                    onClick={copyUrl}
                                    className="w-9 h-9 brut-border rounded-lg bg-[#F3D469] flex items-center justify-center hover:translate-y-[-1px] brut-shadow-sm"
                                    aria-label="copy"
                                >
                                    {copied ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            <button
                                data-testid="share-invite-btn"
                                onClick={share}
                                className="brut-btn w-full justify-center"
                            >
                                <ShareAndroid className="w-4 h-4" /> {t("invite.share")}
                            </button>
                        </div>

                        <div className="mt-4 text-xs text-[#255636] text-center font-medium">
                            {t("invite.footer")}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
